import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { SystemNotificationAdminService, SystemNotificationAdmin } from '../../../services/system-notification-admin.service';
import { SystemDomainService } from '../../../services/system-domain.service';
import { ToastService } from '../../../services/toast-service';

@Component({
  selector: 'app-notification-management',
  templateUrl: './notification-management.component.html',
  styleUrls: ['./notification-management.component.scss']
})
export class NotificationManagementComponent implements OnInit {
  @ViewChild('notificationModal') notificationModal: any;
  @ViewChild('deleteConfirmModal') deleteConfirmModal: any;

  notifications: SystemNotificationAdmin[] = [];
  availableDomains: any[] = [];
  selectedNotification: SystemNotificationAdmin | null = null;
  notificationForm: FormGroup;
  isEditMode = false;
  isLoading = false;
  isSaving = false;
  isAllClients = false;

  // Smart Grid Configuration
  gridColumns: any[] = [];

  notificationTypes = [
    { value: 'info', label: 'Info', icon: 'bi-info-circle' },
    { value: 'success', label: 'Success', icon: 'bi-check-circle' },
    { value: 'warning', label: 'Warning', icon: 'bi-exclamation-triangle' },
    { value: 'danger', label: 'Danger', icon: 'bi-x-circle' }
  ];

  selectedClientDBNames: string[] = [];

  constructor(
    private systemNotificationAdminService: SystemNotificationAdminService,
    private systemDomainService: SystemDomainService,
    private fb: FormBuilder,
    private modalService: NgbModal,
    private translateService: TranslateService,
    private toastService: ToastService
  ) {
    this.notificationForm = this.fb.group({
      notificationID: [null],
      title: ['', [Validators.required, Validators.maxLength(200)]],
      message: ['', [Validators.required]],
      type: ['info', Validators.required],
      priority: [0, [Validators.min(0), Validators.max(100)]],
      startDate: [null, Validators.required],
      endDate: [null],
      isActive: [true],
      displayType: ['alert', Validators.required],
      targetAudience: ['All', Validators.required],
      imageUrl: [''],
      ctaText: [''],
      ctaLink: ['']
    });
  }

  ngOnInit(): void {
    this.buildColumns();
    this.loadNotifications();
    this.loadDomains();
  }

  buildColumns(): void {
    this.gridColumns = [
      { field: 'notificationID', title: this.translateService.instant('SystemAdmin.notificationID'), sortable: true, filterable: true, filterType: 'number', width: '80px' },
      { field: 'title', title: this.translateService.instant('SystemAdmin.notificationTitle'), sortable: true, filterable: true, filterType: 'text', width: '250px' },
      { field: 'type', title: this.translateService.instant('SystemAdmin.notificationType'), sortable: true, filterable: true, filterType: 'text', width: '130px', template: 'type' },
      { field: 'priority', title: this.translateService.instant('SystemAdmin.notificationPriority'), sortable: true, filterable: true, filterType: 'number', width: '90px' },
      { field: 'startDate', title: this.translateService.instant('SystemAdmin.startDate'), sortable: true, width: '170px', template: 'startDate' },
      { field: 'endDate', title: this.translateService.instant('SystemAdmin.endDate'), sortable: true, width: '170px', template: 'endDate' },
      { field: 'isActive', title: this.translateService.instant('SystemAdmin.isActive'), sortable: true, width: '100px', template: 'isActive' },
      { field: 'clientCount', title: this.translateService.instant('SystemAdmin.clientCount'), sortable: true, width: '150px', template: 'clientCount' },
      { field: 'actions', title: this.translateService.instant('General.actions'), sortable: false, width: '120px', template: 'actions' }
    ];
  }

  loadNotifications(): void {
    this.isLoading = true;
    this.systemNotificationAdminService.getAllNotifications().subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          this.notifications = response.result;
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('SystemAdmin.errorLoadingNotifications'));
        }
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading notifications:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingNotifications')]
        });
        this.isLoading = false;
      }
    });
  }

  loadDomains(): void {
    this.systemDomainService.getAllDomains().subscribe({
      next: (domains) => {
        if (domains && Array.isArray(domains)) {
          this.availableDomains = domains;
        }
      },
      error: (error) => {
        console.error('Error loading domains:', error);
      }
    });
  }

  openCreateModal(): void {
    this.isEditMode = false;
    this.selectedNotification = null;
    this.isAllClients = false;
    this.selectedClientDBNames = [];
    
    const now = new Date();
    this.notificationForm.reset({
      type: 'info',
      priority: 0,
      isActive: true,
      startDate: now.toISOString().substring(0, 16),
      displayType: 'alert',
      targetAudience: 'All',
      imageUrl: '',
      ctaText: '',
      ctaLink: ''
    });
    
    this.modalService.open(this.notificationModal, { size: 'lg', backdrop: 'static' });
  }

  openEditModal(notification: SystemNotificationAdmin): void {
    this.isEditMode = true;
    this.selectedNotification = notification;
    
    // Load notification with clients to get assignment info
    this.systemNotificationAdminService.getNotificationWithClients(notification.notificationID!).subscribe({
      next: (response) => {
        if (response?.success && response.result) {
          const data = response.result;
          this.isAllClients = data.isGlobal;
          this.selectedClientDBNames = data.assignedClients || [];
          
          this.notificationForm.patchValue({
            notificationID: notification.notificationID,
            title: notification.title,
            message: notification.message,
            type: notification.type,
            priority: notification.priority,
            startDate: notification.startDate ? new Date(notification.startDate).toISOString().substring(0, 16) : null,
            endDate: notification.endDate ? new Date(notification.endDate).toISOString().substring(0, 16) : null,
            isActive: notification.isActive,
            displayType: notification.displayType || 'alert',
            targetAudience: notification.targetAudience || 'All',
            imageUrl: notification.imageUrl || '',
            ctaText: notification.ctaText || '',
            ctaLink: notification.ctaLink || ''
          });
          
          this.modalService.open(this.notificationModal, { size: 'lg', backdrop: 'static' });
        }
      },
      error: (error) => {
        console.error('Error loading notification details:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorLoadingNotification')]
        });
      }
    });
  }

  saveNotification(): void {
    if (this.notificationForm.invalid) {
      Object.keys(this.notificationForm.controls).forEach(key => {
        this.notificationForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isSaving = true;
    const formValue = this.notificationForm.value;

    const request = {
      notificationID: formValue.notificationID,
      title: formValue.title,
      message: formValue.message,
      type: formValue.type,
      priority: formValue.priority,
      startDate: formValue.startDate,
      endDate: formValue.endDate,
      isActive: formValue.isActive,
      displayType: formValue.displayType || 'alert',
      targetAudience: formValue.targetAudience || 'All',
      imageUrl: formValue.imageUrl || null,
      ctaText: formValue.ctaText || null,
      ctaLink: formValue.ctaLink || null,
      clientDBNames: this.isAllClients ? [] : this.selectedClientDBNames
    };

    const operation = this.isEditMode
      ? this.systemNotificationAdminService.updateNotification(formValue.notificationID, request)
      : this.systemNotificationAdminService.createNotification(request);

    operation.subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant(
              this.isEditMode ? 'SystemAdmin.notificationUpdated' : 'SystemAdmin.notificationCreated'
            )]
          });
          this.modalService.dismissAll();
          this.loadNotifications();
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant(
            this.isEditMode ? 'SystemAdmin.errorUpdatingNotification' : 'SystemAdmin.errorCreatingNotification'
          ));
        }
        this.isSaving = false;
      },
      error: (error) => {
        console.error('Error saving notification:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant(
            this.isEditMode ? 'SystemAdmin.errorUpdatingNotification' : 'SystemAdmin.errorCreatingNotification'
          )]
        });
        this.isSaving = false;
      }
    });
  }

  openDeleteModal(notification: SystemNotificationAdmin): void {
    this.selectedNotification = notification;
    this.modalService.open(this.deleteConfirmModal, { size: 'md', backdrop: 'static' });
  }

  confirmDelete(): void {
    if (!this.selectedNotification?.notificationID) return;

    this.systemNotificationAdminService.deleteNotification(this.selectedNotification.notificationID).subscribe({
      next: (response) => {
        if (response?.success) {
          this.toastService.show({
            type: 'success',
            data: [this.translateService.instant('SystemAdmin.notificationDeleted')]
          });
          this.modalService.dismissAll();
          this.loadNotifications();
        } else {
          this.toastService.showApiError(response?.errorMessage, this.translateService.instant('SystemAdmin.errorDeletingNotification'));
        }
      },
      error: (error) => {
        console.error('Error deleting notification:', error);
        this.toastService.show({
          type: 'error',
          data: [this.translateService.instant('SystemAdmin.errorDeletingNotification')]
        });
      }
    });
  }

  toggleAllClients(): void {
    this.isAllClients = !this.isAllClients;
    if (this.isAllClients) {
      this.selectedClientDBNames = [];
    }
  }

  toggleClientSelection(clientDBName: string): void {
    const index = this.selectedClientDBNames.indexOf(clientDBName);
    if (index > -1) {
      this.selectedClientDBNames.splice(index, 1);
    } else {
      this.selectedClientDBNames.push(clientDBName);
    }
  }

  isClientSelected(clientDBName: string): boolean {
    return this.selectedClientDBNames.includes(clientDBName);
  }

  getTypeBadgeClass(type: string): string {
    switch (type?.toLowerCase()) {
      case 'info':
        return 'badge-info';
      case 'success':
        return 'badge-success';
      case 'warning':
        return 'badge-warning';
      case 'danger':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  getTypeIcon(type: string): string {
    const typeObj = this.notificationTypes.find(t => t.value === type);
    return typeObj?.icon || 'bi-info-circle';
  }
}
